import { Canvas, Textbox } from 'fabric';

import { TEXT_SCALING_CONFIG } from './draw-text-scaling.constants';

/**
 * Scales a textbox's font size down iteratively until it fits within the specified height.
 * Also applies left alignment and vertical centering within the original bounds.
 *
 * This is used for non-English translations where the translated text may be longer
 * than the original English text and wrap beyond the original textbox bounds.
 *
 * @param canvas - The Fabric.js canvas instance to trigger re-rendering
 * @param text - The Fabric.js Textbox to scale
 * @param originalHeight - The original height of the textbox in pixels
 * @param minFontSize - Minimum font size to scale down to (default from config)
 * @param scaleFactor - Amount to reduce font size per iteration (default from config)
 * @returns Promise that resolves after canvas has re-rendered the scaled text
 *
 * @example
 * // English: "Hello" fits in 1 line at 16px (height: 20px)
 * // Spanish: "Hola, ¿cómo estás?" wraps to 2 lines (height: 40px)
 * // Result: Font scaled down until text fits in 20px height
 */
export const scaleTextToFit = async (
  canvas: Canvas,
  text: Textbox,
  originalHeight: number,
  minFontSize: number = TEXT_SCALING_CONFIG.MIN_FONT_SIZE,
  scaleFactor: number = TEXT_SCALING_CONFIG.SCALE_FACTOR
): Promise<void> => {
  let currentFontSize = text.fontSize || 16;
  let iterations = 0;

  // Iteratively reduce font size until text fits or reaches minimum
  while (
    text.calcTextHeight() > originalHeight &&
    currentFontSize > minFontSize &&
    iterations < TEXT_SCALING_CONFIG.SCALE_ITERATION_LIMIT
  ) {
    currentFontSize = Math.max(minFontSize, currentFontSize * scaleFactor);
    text.set({ fontSize: currentFontSize });
    text.initDimensions();
    iterations++;
  }

  // Calculate vertical centering offset
  const finalTextHeight = text.calcTextHeight();
  const verticalOffset = (originalHeight - finalTextHeight) / 2;
  const currentTop = text.top || 0;

  // Apply left alignment and adjust position for vertical centering
  text.set({
    textAlign: 'left',
    top: currentTop + verticalOffset,
  });

  // Development logging for debugging
  if (process.env.NODE_ENV === 'development' && iterations > 0) {
    console.log(
      `[Draw Text Scaling] Scaled text from ${text.fontSize}px to ${currentFontSize}px in ${iterations} iterations`
    );
  }

  // Force canvas to re-render the scaled text
  canvas.requestRenderAll();

  // Wait for next frame to ensure rendering is complete before HTML conversion
  await new Promise(resolve => requestAnimationFrame(() => resolve(true)));
};
